<script setup lang="ts">
import type { UserLimit, UserLimitPayload } from '@/types/UserLimits';

const { $containment } = useNuxtApp();
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { getDateTime } = useDateTimeHelper();
const { showMoneyFormat } = useWallet();
const { userLimits, setAccountLimits } = useUserLimits();
const { userCurrency, userCountry, userLanguage } = useUserData();

const { choiceOptions, periodsOptions, getPeriodLabel, getTimeToNextChange } =
  useAccountProtection();

const userLimitBet = ref<UserLimit>({
  active: 0,
  period: '0',
  amount: 0,
});

const isLoading = ref(false);

const formattedScheduledAmount = computed(() => {
  return Number(userLimits.value?.user_limit_bet_next_amount_change) > 0
    ? moneyFormatter(userLimits.value?.user_limit_bet_next_amount_change)
    : t('general.unlimited');
});

const moneyFormatter = (value?: string | number) => {
  if (!value) {
    return;
  }

  return showMoneyFormat({
    value: Number(value) / 100,
  });
};

async function handleSubmit() {
  if (isLoading.value) {
    return;
  }

  if (userLimitBet.value.active) {
    let message;

    if (!Number(userLimitBet.value.period)) {
      message = t('account.account_protection.select_period');
    } else if (!Number(userLimitBet.value.amount)) {
      message = t('account.account_protection.type_amount');
    }

    if (message) {
      openToaster({
        message,
        type: 'error',
      });

      return;
    }
  }

  await $containment.handle('update_limits', () => {
    sendSubmit();
  });
}

async function sendSubmit() {
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  isLoading.value = true;

  const payload: UserLimitPayload = {
    type: 'bet',
  };

  if (Number(userLimitBet.value.active)) {
    payload.user_limit_bet_active = Number(userLimitBet.value.active);
    payload.user_limit_bet_period = Number(userLimitBet.value.period);
    payload.user_limit_bet = Number(userLimitBet.value.amount);
  } else {
    payload.user_limit_bet_active = 0;
  }

  if ($kyc?.current?.id) {
    payload.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    payload.password_check = temporaryPassword.get();
  }

  await setAccountLimits(payload)
    .then((res: any) => {
      const message =
        res.data?.message || t('account.account_protection.update_success');

      openToaster({
        message,
        type: 'success',
      });

      userLimitBet.value.period = String(
        userLimits.value?.user_limit_bet_period,
      );
      userLimitBet.value.amount = userLimits.value?.user_limit_bet ?? 0;
    })
    .catch((error: any) => {
      const message =
        error.data?.message ||
        error.message ||
        t('account.account_protection.generic_error');

      openToaster({
        message,
        type: 'error',
      });
    })
    .finally(() => {
      isLoading.value = false;
      temporaryPassword.clear();
    });
}

const setUserLimits = () => {
  if (userLimits.value?.spa_limit_bet) {
    userLimitBet.value.active = 1;
    userLimitBet.value.period = '4';
    userLimitBet.value.amount = Number(userLimits.value.spa_limit_bet);
  } else {
    if (userLimits.value?.user_limit_bet_active) {
      userLimitBet.value.active = Number(
        userLimits.value?.user_limit_bet_active,
      );
    }

    if (userLimits.value?.user_limit_bet_period) {
      userLimitBet.value.period = String(
        userLimits.value?.user_limit_bet_period,
      );
    }

    if (userLimits.value?.user_limit_bet) {
      userLimitBet.value.amount = Number(userLimits.value?.user_limit_bet);
    }
  }
};

onMounted(() => {
  setUserLimits();
});
</script>

<template>
  <div :class="$style.betLimit">
    <BaseLoading
      class="!bg-auth-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <div :class="$style.groupRadio">
      <label>
        {{ $t('account.account_protection.set_limit') }}
      </label>

      <BaseFormRadio
        v-model="userLimitBet.active"
        :options="choiceOptions"
      />
    </div>

    <div
      v-if="!!userLimitBet.active"
      :class="$style.groupFields"
    >
      <div :class="$style.groupInput">
        <BaseFormSelectField
          id="user_limit_bet_period"
          v-model="userLimitBet.period"
          :initial-value="userLimitBet.period"
          name="user_limit_bet_period"
          :options="periodsOptions"
        />
      </div>

      <div :class="$style.groupInput">
        <BaseFormInputCurrency
          id="userLimitBet"
          v-model="userLimitBet.amount"
          name="value"
          required
          :show-currency="true"
          size="lg"
          :user-country="userCountry"
          :user-currency="userCurrency"
          :user-language="userLanguage"
        />
      </div>
    </div>

    <div
      v-if="
        userLimits?.user_limit_bet_next_amount_change &&
        userLimits?.user_limit_bet_next_amount_change_requested_at
      "
      :class="$style.lastChange"
    >
      <p>
        <strong>{{ $t('general.active') }}:</strong>
        {{ moneyFormatter(userLimits?.user_limit_bet) }} -
        {{ getDateTime(userLimits?.user_limit_bet_change) }}
      </p>

      <p
        v-html="
          $t('account.account_protection.user_limit_scheduled_change', {
            amount: formattedScheduledAmount,
            period: getPeriodLabel(
              userLimits?.user_limit_bet_next_period_change,
            ),
            requestedAt: getDateTime(
              userLimits?.user_limit_bet_next_amount_change_requested_at,
            ),
            activeAt: getTimeToNextChange(userLimits?.user_limit_bet_change),
          })
        "
      />
    </div>

    <button
      :class="$style.btnSubmit"
      @click="handleSubmit()"
    >
      <NuxtIcon name="check" />
      {{ $t('account.save') }}
    </button>

    <div
      v-if="!!moneyFormatter(userLimits?.spa_limit_bet)"
      class="alert alert-warning"
      :class="$style.alertSpa"
    >
      <p
        v-html="
          $t('account.account_protection.spa_limit_alert', {
            limit: t('account.account_protection.user_limit_bet'),
            value: moneyFormatter(userLimits?.spa_limit_bet),
          })
        "
      ></p>
    </div>
  </div>
</template>

<style module lang="postcss">
.betLimit {
  @apply w-full;

  .groupRadio {
    @apply flex items-center gap-5 font-semibold text-xs md:text-sm text-texts;
  }

  .groupFields {
    @apply flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-5 mt-5;

    .groupInput {
      @apply w-full min-w-40 md:max-w-52;
    }
  }

  .lastChange {
    @apply text-2xs text-warning/70 mt-4;
  }

  .alertSpa {
    @apply mt-4 text-xs;

    strong {
      &:first-child {
        @apply lowercase;
      }
    }
  }

  .btnSubmit {
    @apply bg-button-bg text-button-text py-2 px-4 rounded-md text-sm font-semibold whitespace-nowrap flex items-center justify-center gap-2 mt-5 disabled:opacity-40 disabled:cursor-not-allowed;
  }
}
</style>
